/**
 * @file src/api/settingsApi.js
 * @description The signed-in user's settings — general (locale, themeMode), design (theme + fonts).
 * Orbiter Studio uses this for CHROME only. It resolves the inspector shell from
 * `design.theme.id`, matching plantasia.space-root; stale `label`/`variantMap` fields are ignored
 * unless the id no longer exists. The orbiter's own content theme is separate and saved on the
 * orbiter entity. See planning/theme-architecture/PLAN.md.
 */
import { fetchJsonFromApi } from './httpClient.js';
import { resolveViewerAuth, viewerCacheKey } from '../auth/viewerAuth.js';
import { configurationEpoch, isConfigurationEpochCurrent } from '../config/configurationPack.js';

/**
 * The one read of the settings document this page has made, shared by every module that wants a
 * slice of it.
 *
 * Five modules each read one field of the same document at boot, and each used to fetch the whole
 * thing for itself — an authed GET the browser cannot cache, so five real round trips. The first
 * caller's request now answers all of them, and a successful answer is kept for the life of the page.
 *
 * Handed only to a request made as the same viewer (`viewerCacheKey`) and within the same
 * configuration identity epoch, which both identity edges in `sessionAuth.js` advance. The viewer is
 * what catches an embed host's token arriving or changing, which re-fetches before that edge fires;
 * the epoch is what catches the rest. A failed read (null — a guest, a blip) is not kept, so the next
 * caller asks again; and a save drops the entry, so nothing reads past a write.
 * @type {{ epoch: number, viewer: string, promise: Promise<object|null> } | null}
 */
let shared = null;

function normalizeUserSettingsPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const candidate = payload.settings && typeof payload.settings === 'object'
    ? payload.settings
    : payload;
  return candidate && typeof candidate === 'object' ? candidate : null;
}

async function requestUserSettings(authToken) {
  try {
    const response = await fetchJsonFromApi('/me/users/settings', {
      method: 'GET',
      authToken,
      credentials: 'include',
    });
    if (!response || !response.ok) return null;
    return normalizeUserSettingsPayload(await response.json());
  } catch {
    return null;
  }
}

/** The shared answer, handed to one caller — who may stop waiting without cancelling it for the rest. */
function untilAborted(promise, signal) {
  if (!signal) return promise;
  return new Promise((resolve) => {
    const onAbort = () => resolve(null);
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then((value) => {
      signal.removeEventListener('abort', onAbort);
      resolve(value);
    });
  });
}

/**
 * The signed-in user's settings, or null when unauthenticated / unavailable.
 *
 * Answered from this page's one read when it has one — see {@link shared}. `fresh` asks the server
 * again regardless, for the caller whose answer can change somewhere this page cannot see (another
 * tab), and what it finds becomes the page's answer.
 * @param {{ signal?: AbortSignal, fresh?: boolean }} options
 * @returns {Promise<object|null>}
 */
export async function fetchUserSettings({ signal, fresh = false } = {}) {
  let auth;
  try {
    auth = await resolveViewerAuth();
  } catch {
    return null;
  }
  if (signal?.aborted) return null;
  const viewer = viewerCacheKey(auth);
  if (fresh || shared?.viewer !== viewer || !isConfigurationEpochCurrent(shared.epoch)) {
    const entry = { epoch: configurationEpoch(), viewer, promise: requestUserSettings(auth.authToken) };
    shared = entry;
    // Only if it is still OURS: a save, a fresher read or another viewer's read may have replaced it
    // while this one was out, and clearing that would throw away the newer answer.
    entry.promise.then((settings) => {
      if (!settings && shared === entry) shared = null;
    });
  }
  return untilAborted(shared.promise, signal);
}

/**
 * Merge-save a slice of the signed-in user's settings. PUT with `?merge=true` so we send only the
 * keys we own — a whole-object write from the orbiter would clobber preferences root manages
 * (design fonts, locale, privacy) that this app never loads.
 *
 * Returns the saved settings, or null when unauthenticated / unavailable. Callers treat a null as
 * "not persisted for this account" and keep their local value: the toggle must not appear to fail
 * for a guest, who legitimately has nowhere to save it.
 *
 * @param {object} patch - a partial settings object, e.g. `{ usage: { performanceMode: true } }`
 * @param {{ signal?: AbortSignal }} options
 * @returns {Promise<object|null>}
 */
export async function saveUserSettings(patch, { signal } = {}) {
  if (!patch || typeof patch !== 'object') return null;
  try {
    const { authToken } = await resolveViewerAuth();
    const response = await fetchJsonFromApi('/me/users/settings?merge=true', {
      method: 'PUT',
      signal,
      authToken,
      credentials: 'include',
      body: patch,
    });
    if (!response || !response.ok) return null;
    return normalizeUserSettingsPayload(await response.json());
  } catch {
    return null;
  } finally {
    // Once the write is DONE, not when it starts — a read issued while it was out can come back
    // with the document from before it. And whether or not it landed: a write that failed on the
    // way back may still have been applied, and the next read is what finds out.
    shared = null;
  }
}
